//-tech-debt-react-query;
import { DefaultOptions, QueryCache, QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { getCookie } from './cookieHelper';
import { refreshOn401 } from './queriesHelpers';
import { store } from './store';

const api = process.env.REACT_APP_API;

// Env vars are strings, so a bare truthiness test treats "false" as on.
const mockApi = (process.env.REACT_APP_MOCK_SF_API ?? '').toLowerCase() === 'true';

interface RequestOptions {
  token?: string | null;
  credentials?: RequestCredentials;
  page?: number;
  size?: number;
  pagination?: boolean;
  body?: any;
  includePageSize?: boolean;
}

interface IDItem {
  id?: number | string;
  Id?: number | string;
  [key: string]: any;
}

// ------------------------------
// URL normalization / consistency
// ------------------------------

const normalizeEndpoint = (endpoint: string) => {
  let p = endpoint || '';
  try {
    if (p.startsWith('http://') || p.startsWith('https://')) {
      p = new URL(p).pathname;
    }
  } catch {
    // ignore
  }

  p = p.replace(/^\/+/, ''); // trim leading slash
  if (p.startsWith('api/')) p = p.slice(4); // avoid duplicated /api/api/

  return p;
};

const withTrailingSlash = (path: string) => (path.endsWith('/') ? path : `${path}/`);
const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '');

const normalizeEndpointForMethod = (endpoint: string, method: string) => {
  const ep = normalizeEndpoint(endpoint);
  const m = method.toUpperCase();

  // In this backend, collection-style endpoints generally use trailing slash.
  // This avoids 301 redirects like /db/opportunity?page=.. -> /db/opportunity/?page=..
  if (['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(m)) {
    return withTrailingSlash(ep);
  }

  return ep;
};

const shouldRefreshOn401 = (endpoint?: string) => {
  const normalized = normalizeEndpoint(endpoint || '');

  // Do not reload whole app for optional/integration tile sources.
  if (
    normalized.startsWith('dcloud/') ||
    normalized.startsWith('snowflake/') ||
    normalized.startsWith('jira-cloud/') ||
    normalized.startsWith('moodle/') ||
    normalized.startsWith('queue/')
  ) {
    return false;
  }

  return true;
};

/**
 * A cookie under the branch-prefixed name this app writes, or the plain name.
 *
 * `getCookie` already applies the prefix, so passing it a prefixed name would look up
 * `dev_dev_tb_jwt` and never match. The plain read is the one that matters: a token set
 * by the backend or another tool does not carry our branch prefix.
 */
const getBranchOrPlainCookie = (name: string) => getCookie(name) || Cookies.get(name);

/**
 * Whether a JWT's own exp claim has already passed. See the twin of this in
 * queriesHelpers.ts for why the persisted token needs skipping rather than trusting.
 */
const isExpiredJwt = (token: string) => {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return false;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));

    return typeof payload?.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

/** The first candidate that is present and not a spent JWT. */
const firstUsableToken = (candidates: (string | null | undefined)[]) => {
  for (const candidate of candidates) {
    if (candidate && !isExpiredJwt(candidate)) {
      return candidate;
    }
  }

  return null;
};

const getToolboxToken = () => {
  const state: any = store.getState();

  return firstUsableToken([
    state?.user?.toolboxToken,
    getBranchOrPlainCookie('tb_jwt'),
    getBranchOrPlainCookie('toolbox_jwt'),
    getBranchOrPlainCookie('tb_token'),
    getBranchOrPlainCookie('toolbox_token'),
    getBranchOrPlainCookie('toolboxToken'),
  ]);
};

const getSalesforceToken = () => {
  const state: any = store.getState();

  return state?.user?.sfToken || getBranchOrPlainCookie('sf_token') || null;
};

const isToolboxTokenEndpoint = (endpoint: string) => {
  const normalized = normalizeEndpoint(endpoint);

  return (
    normalized.startsWith('db/') ||
    normalized.startsWith('sf/') ||
    normalized.startsWith('ai/') ||
    normalized.startsWith('queue/') ||
    normalized.startsWith('jira/') ||
    normalized.startsWith('jira-cloud/') ||
    normalized.startsWith('moodle/') ||
    normalized.startsWith('dcloud/') ||
    normalized.startsWith('snowflake/') ||

    normalized.startsWith('auth/client/') ||
    normalized === 'auth/client' ||
    normalized.startsWith('auth/authz/') ||
    normalized.startsWith('auth/authorize/') ||
    normalized.startsWith('auth/token/validate') ||
    normalized.startsWith('auth/token/revoke') ||
    normalized.startsWith('auth/logout') ||
    normalized.startsWith('auth/session/validate')
  );
};

const buildQueryParams = (params: Record<string, any>) => {
  // sort keys for deterministic URL ordering
  const query = new URLSearchParams();
  Object.keys(params || {})
    .sort()
    .forEach((key) => {
      const val = params[key];
      if (val !== undefined && val !== null) query.append(key, String(val));
    });
  return query.toString();
};

const buildUrl = (
  endpoint: string,
  params: Record<string, any> = {},
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET'
) => {
  const baseUrl = api;
  const ep = normalizeEndpointForMethod(endpoint, method);
  const qs = buildQueryParams(params);
  return qs ? `${baseUrl}/${ep}?${qs}` : `${baseUrl}/${ep}`;
};

// ------------------------------
// Lightweight request dedupe/cache
// ------------------------------

const inFlightRequests = new Map<string, Promise<any>>();
const getResponseCache = new Map<string, { ts: number; data: any }>();
const GET_CACHE_TTL_MS = 30_000; // keep short to reduce stale risk

const makeRequestKey = (
  method: string,
  endpoint: string,
  params: Record<string, any>,
  body?: any
) => {
  const normalizedMethod = method.toUpperCase();
  const normalizedEndpoint = normalizeEndpointForMethod(endpoint, normalizedMethod);
  const sortedParams = buildQueryParams(params || {});
  const bodyString = body == null ? '' : JSON.stringify(body);
  return `${normalizedMethod}|${normalizedEndpoint}|${sortedParams}|${bodyString}`;
};

// 401 handling lives in queriesHelpers.refreshOn401 (imported above): a single shared
// implementation that raises the re-authentication modal over the current page instead of
// resetting the store and redirecting to the login screen. This module used to carry a
// duplicate copy that reset() + window.location.href = '/'.

export const getApiHeaders = (
  token: string | null = null,
  method = 'GET',
  endpoint?: string
) => {
  const toolboxToken = getToolboxToken();
  const salesforceToken = getSalesforceToken();

  let effectiveToken = token;
  let effectiveTokenKind: 'toolbox' | 'salesforce' | 'explicit' | null = null;

  if (effectiveToken) {
    effectiveTokenKind = 'explicit';
  } else if (endpoint && isToolboxTokenEndpoint(endpoint)) {
    effectiveToken = toolboxToken || salesforceToken;
    effectiveTokenKind = toolboxToken ? 'toolbox' : salesforceToken ? 'salesforce' : null;
  } else {
    effectiveToken = toolboxToken || salesforceToken;
    effectiveTokenKind = toolboxToken ? 'toolbox' : salesforceToken ? 'salesforce' : null;
  }

  if (mockApi) {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'react-app-mock-sf-api': 'true',
      Origin: 'http://localhost:3001',
      ...(effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {}),
      ...(effectiveTokenKind === 'toolbox'
        ? { 'X-Toolbox-Authorization': `Bearer ${effectiveToken}` }
        : {}),
      ...(effectiveTokenKind === 'salesforce'
        ? { 'X-Salesforce-Authorization': `Bearer ${effectiveToken}` }
        : {}),
    };
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (effectiveToken) {
    headers.Authorization = `Bearer ${effectiveToken}`;

    if (effectiveTokenKind === 'toolbox') {
      headers['X-Toolbox-Authorization'] = `Bearer ${effectiveToken}`;
    }

    if (effectiveTokenKind === 'salesforce') {
      headers['X-Salesforce-Authorization'] = `Bearer ${effectiveToken}`;
    }

    // Both tokens when we hold both, so a JWT the backend cannot verify falls through to the
    // legacy Salesforce path instead of 401ing. See the twin of this in queriesHelpers.ts.
    if (effectiveTokenKind === 'toolbox' && salesforceToken) {
      headers['X-Salesforce-Authorization'] = `Bearer ${salesforceToken}`;
    }
  }

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const csrfToken = Cookies.get('csrftoken');
    if (csrfToken) headers['X-CSRFToken'] = csrfToken;
  }

  return headers;
};

const queryConfig = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 5, // 5m (was 1h)
  gcTime: 1000 * 60 * 30, // 30m
  retry: 1, // reduce retry storms on slow endpoints
  retryDelay: 2000,
};

const defaultOptions: DefaultOptions = {
  queries: queryConfig,
  mutations: {
    retry: 1,
    retryDelay: 2000,
  },
};

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error('Query error: ', error, query);
    },
  }),
  defaultOptions: defaultOptions,
});

// ------------------------------
// HTTP helpers
// ------------------------------

export const fetchStream = async (endpoint: string, body: any, token: string | null = null) => {
  const url = buildUrl(endpoint, {}, 'POST');

  const resp = await fetch(url, {
    method: 'POST',
    headers: getApiHeaders(token, 'POST', endpoint),
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (resp.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || resp.statusText);
  }
  if (!resp.body) {
    throw new Error('Streaming not supported by browser/response');
  }

  return resp;
};

const fetchItems = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestOptions = {}
) => {
  if (!endpoint) {
    return { items: [], total_count: 0 };
  }

  const {
    page = 1,
    size = 1000,
    token = null,
    credentials = 'include',
    pagination = false,
    body = null,
    includePageSize = true,
  } = options;

  const method = body ? 'POST' : 'GET';
  const queryParamsObj = includePageSize ? { page, size, ...params } : { ...params };
  const key = makeRequestKey(method, endpoint, queryParamsObj, body);

  // GET response cache
  if (method === 'GET') {
    const cached = getResponseCache.get(key);
    if (cached && Date.now() - cached.ts < GET_CACHE_TTL_MS) {
      const data = cached.data;
      if (pagination) return data;
      if (data && typeof data === 'object') {
        if ('items' in data) return (data as any).items;
        if ('records' in data) return (data as any).records;
      }
      return data;
    }
  }

  // in-flight dedupe (GET/POST)
  if (inFlightRequests.has(key)) {
    const data = await inFlightRequests.get(key)!;
    if (pagination) return data;
    if (data && typeof data === 'object') {
      if ('items' in data) return (data as any).items;
      if ('records' in data) return (data as any).records;
    }
    return data;
  }

  const requestPromise = (async () => {
    const url = buildUrl(endpoint, queryParamsObj, method);

    const response = await fetch(url, {
      headers: getApiHeaders(token, method, endpoint),
      credentials: credentials as RequestCredentials,
      ...(body ? { body: JSON.stringify(body), method: 'POST' as const } : {}),
    });

    if (response.status === 401) {
      if (shouldRefreshOn401(endpoint)) {
        await refreshOn401();
      }
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      console.error('Failed to fetch items:', response.statusText);
      throw new Error(response.statusText);
    }

    return response.json();
  })();

  inFlightRequests.set(key, requestPromise);

  try {
    const data = await requestPromise;

    if (method === 'GET') {
      getResponseCache.set(key, { ts: Date.now(), data });
    }

    if (pagination) return data;
    if (data && typeof data === 'object') {
      if ('items' in data) return (data as any).items;
      if ('records' in data) return (data as any).records;
    }
    return data;
  } finally {
    inFlightRequests.delete(key);
  }
};

const postItem = async <T>(
  endpoint: string,
  item: T,
  params: Record<string, any> = {},
  token: string | null = null
) => {
  if (!endpoint) throw new Error('Endpoint is required');

  const url = buildUrl(endpoint, params, 'POST');

  const response = await fetch(url, {
    headers: getApiHeaders(token, 'POST', endpoint),
    method: 'POST',
    body: JSON.stringify(item),
    credentials: 'include',
  });

  if (response.status === 401) {
    if (shouldRefreshOn401(endpoint)) {
      await refreshOn401();
    }
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to post item:', response.statusText);
    throw new Error(response.statusText);
  }

  return response.json();
};

const updateItem = async <T extends IDItem>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  item: T,
  token: string | null = null
) => {
  const itemId = item.id ?? item.Id;
  if (!itemId && ['PUT', 'PATCH'].includes(method)) {
    throw new Error('Item does not have an id or Id property');
  }

  const base = normalizeEndpointForMethod(endpoint, method);

  // For PUT/PATCH target item endpoint, keep trailing slash
  const targetEndpoint =
    method === 'PUT' || method === 'PATCH'
      ? `${withoutTrailingSlash(base)}/${itemId}/`
      : base;

  const url = `${api}/${targetEndpoint}`;

  const payload = { ...item };
  if (method === 'PATCH' && payload.Id !== undefined) {
    delete payload.Id;
  }

  const response = await fetch(url, {
    headers: getApiHeaders(token, method, endpoint),
    method,
    body: ['POST', 'PUT', 'PATCH'].includes(method) ? JSON.stringify(payload) : undefined,
    credentials: 'include',
  });

  if (response.status === 401) {
    if (shouldRefreshOn401(endpoint)) {
      await refreshOn401();
    }
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to update item:', response.statusText);
    throw new Error(response.statusText);
  }

  return response.json();
};

const deleteItem = async (endpoint: string, token: string | null = null) => {
  const url = buildUrl(endpoint, {}, 'DELETE');

  const response = await fetch(url, {
    headers: getApiHeaders(token, 'DELETE', endpoint),
    method: 'DELETE',
    credentials: 'include',
  });

  if (response.status === 401) {
    if (shouldRefreshOn401(endpoint)) {
      await refreshOn401();
    }
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to delete item:', response.statusText);
    throw new Error(response.statusText);
  }

  return response.ok;
};

const fetchItem = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestOptions = {}
) => {
  if (!endpoint) throw new Error('Endpoint is required');

  const { token = null, credentials = 'include' } = options;
  const url = buildUrl(endpoint, params, 'GET');

  const response = await fetch(url, {
    headers: getApiHeaders(token, 'GET', endpoint),
    credentials: credentials as RequestCredentials,
  });

  if (response.status === 401) {
    if (shouldRefreshOn401(endpoint)) {
      await refreshOn401();
    }
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to fetch item:', response.statusText);
    throw new Error(response.statusText);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Received non-JSON response');
  }

  return response.json();
};

const patchItemRaw = async (endpoint: string, payload: any) => {
  const url = buildUrl(endpoint, {}, 'PATCH');
  const response = await fetch(url, {
    headers: getApiHeaders(null, 'PATCH', endpoint),
    method: 'PATCH',
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const putItemRaw = async (endpoint: string, payload: any) => {
  const url = buildUrl(endpoint, {}, 'PUT');
  const response = await fetch(url, {
    headers: getApiHeaders(null, 'PUT', endpoint),
    method: 'PUT',
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const postItemRaw = async (endpoint: string, payload: any) => {
  const url = buildUrl(endpoint, {}, 'POST');
  const response = await fetch(url, {
    headers: getApiHeaders(null, 'POST', endpoint),
    method: 'POST',
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const putItem = async <T extends IDItem>(endpoint: string, item: T) => {
  return updateItem(endpoint, 'PUT', item);
};

const patchItem = async <T extends IDItem>(endpoint: string, item: T) =>
  updateItem(endpoint, 'PATCH', item);

const buildApiUrl = (endpoint: string, params: Record<string, any> = {}) => {
  return buildUrl(endpoint, params, 'GET');
};

export {
  queryConfig,
  fetchItems,
  fetchItem,
  queryClient,
  postItem,
  postItemRaw,
  putItemRaw,
  patchItemRaw,
  putItem,
  patchItem,
  deleteItem,
  buildApiUrl,
};